import React, { useEffect, useState } from "react"
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    Button,
    Typography,
    CircularProgress,
    Box,
    Chip,
    FormControl,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox
} from "@mui/material"
import {
    fetchLeaveBalanceReports,
    exportLeaveBalanceReports
} from "../../Actions/commonController"
import PaginationTable from "../../Components/Pagination"
import FilterListIcon from "@mui/icons-material/FilterList"
import DownloadIcon from "@mui/icons-material/Download"
import { toast } from "react-toastify"
import { useSearchParams } from 'react-router-dom'
import SearchBar from "../../Components/SearchBar"
import CloseIcon from "@mui/icons-material/Close"
import { IconButton } from "@mui/material"
import moment from "moment"

// Field options for leave balance reports
const fieldOptions = [
    { id: "empid", label: "EmpID" },
    { id: "employee_name", label: "Employee Name" },
    { id: "department", label: "Department" },
    { id: "dateofjoin", label: "Date of Join" },
    { id: "probation_period", label: "Probation Period" },
    { id: "annual_balance", label: "Annual Leave" },
    { id: "sick_balance", label: "Sick Leave" },
    { id: "maternity_balance", label: "Maternity Leave" },
    { id: "paternity_balance", label: "Paternity Leave" },
    { id: "emergency_balance", label: "Emergency Leave" }
]

// Table columns for leave balance reports
const columns = [
    // { id: "id", label: "SR NO.", minWidth: 65 },
    { id: "empid", label: "EMP ID", minWidth: 70 },
    { id: "employee_name", label: "EMPLOYEE NAME", minWidth: 120 },
    { id: "department", label: "DEPARTMENT", minWidth: 100 },
    { id: "dateofjoin", label: "DATE OF JOIN", minWidth: 100 },
    { id: "probation_period", label: "PROBATION PERIOD", minWidth: 120 },
    { id: "annual_balance", label: "ANNUAL", minWidth: 80 },
    { id: "sick_balance", label: "SICK", minWidth: 80 },
    { id: "maternity_balance", label: "MATERNITY", minWidth: 80 },
    { id: "paternity_balance", label: "PATERNITY", minWidth: 80 },
    { id: "emergency_balance", label: "EMERGENCY", minWidth: 80 }
];

// Function to calculate probation period
const calculateProbationPeriod = (dateOfJoin) => {
    if (!dateOfJoin) return 'N/A';

    try {
        // Parse DD/MM/YYYY format
        const [day, month, year] = dateOfJoin.split('/').map(Number);
        const joinDate = new Date(year, month - 1, day);
        const today = new Date();

        if (isNaN(joinDate.getTime())) return 'Invalid Date';

        let years = today.getFullYear() - joinDate.getFullYear();
        let months = today.getMonth() - joinDate.getMonth();
        let days = today.getDate() - joinDate.getDate();

        // Adjust for negative days
        if (days < 0) {
            months--;
            // Get days in previous month
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += prevMonth.getDate();
        }

        // Adjust for negative months
        if (months < 0) {
            years--;
            months += 12;
        }

        return `${years}y ${months}m ${days}d`;
    } catch (error) {
        console.error('Error calculating probation period:', error);
        return 'Error';
    }
};

const calculateLeaveEntitlements = (dateOfJoin, leaveBalances, gender = 'unknown') => {
    if (!dateOfJoin || !leaveBalances) {
        return {
            annual: { display: '0/0' },
            sick: { display: '0/0' },
            maternity: { display: '-' },
            paternity: { display: '-' },
            emergency: { display: '0/0' }
        };
    }

    try {
        const joinDate = moment(dateOfJoin, 'DD/MM/YYYY');
        const today = moment();

        if (!joinDate.isValid()) {
            return {
                annual: { display: '0/0' },
                sick: { display: '0/0' },
                maternity: { display: '-' },
                paternity: { display: '-' },
                emergency: { display: '0/0' }
            };
        }

        // CORRECTED: Calculate complete years of service (floor the decimal years)
        const yearsOfService = Math.floor(today.diff(joinDate, 'years', true));

        // CORRECTED: Use the same logic as backend
        // Annual Leave: 30 base + (complete_years × 30)
        const annualTotal = 30 + (yearsOfService * 30);

        // Sick Leave: 90 base + (complete_years × 90)
        const sickTotal = 90 + (yearsOfService * 90);

        // Maternity Leave: Only for females
        const isFemale = gender && gender.toLowerCase() === 'female';
        const maternityTotal = isFemale ? 60 : 0;

        // Paternity Leave: Only for males
        const isMale = gender && gender.toLowerCase() === 'male';
        const paternityTotal = isMale ? 5 : 0;

        // Emergency leave: Show actual value (no fixed total)
        const emergencyTotal = leaveBalances.emergency?.total || 0;

        // Get the actual leave balances from backend
        const annualActual = leaveBalances.annual?.remaining || 0;
        const sickActual = leaveBalances.sick?.remaining || 0;
        const maternityActual = leaveBalances.maternity?.remaining || 0;
        const paternityActual = leaveBalances.paternity?.remaining || 0;
        const emergencyActual = leaveBalances.emergency?.remaining || 0;

        // Calculate display values
        const annualDisplay = `${annualActual} / ${annualTotal}`;
        const sickDisplay = `${sickActual} / ${sickTotal}`;
        const emergencyDisplay = `${emergencyActual} / ${emergencyTotal}`;

        // Maternity and paternity show "-" if not eligible
        const maternityDisplay = isFemale
            ? `${maternityActual} / ${maternityTotal}`
            : '-';

        const paternityDisplay = isMale
            ? `${paternityActual} / ${paternityTotal}`
            : '-';

        return {
            annual: { display: annualDisplay },
            sick: { display: sickDisplay },
            maternity: { display: maternityDisplay },
            paternity: { display: paternityDisplay },
            emergency: { display: emergencyDisplay }
        };
    } catch (error) {
        console.error('Error calculating leave entitlements:', error);
        return {
            annual: { display: '0/0' },
            sick: { display: '0/0' },
            maternity: { display: '-' },
            paternity: { display: '-' },
            emergency: { display: '0/0' }
        };
    }
};

export default function LeaveBalanceReportsTable() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [todayDate, setTodayDate] = useState('');

    // Get parameters from URL
    const urlPage = parseInt(searchParams.get('page')) || 1
    const urlDepartment = searchParams.get('department') || "all"
    const urlSearch = searchParams.get('search') || ""
    const urlQuantity = parseInt(searchParams.get('quantity')) || 10

    const [page, setPage] = useState(urlPage)
    const [quantity, setQuantity] = useState(urlQuantity)
    const [totalItems, setTotalItems] = useState(0)
    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const [fieldDialogOpen, setFieldDialogOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState(urlSearch)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)
    const [filtersApplied, setFiltersApplied] = useState(false)
    const [filters, setFilters] = useState({
        department: urlDepartment
    })
    const [filterOptions, setFilterOptions] = useState({
        departments: []
    })
    const [monthYearDialogOpen, setMonthYearDialogOpen] = useState(false)
    const [selectedMonth, setSelectedMonth] = useState("all")
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())

    // Initialize with empid always selected
    const [selectedFields, setSelectedFields] = useState(
        fieldOptions.map(f => f.id)
    )

    // Set today's date on component mount
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        setTodayDate(formattedDate);
    }, []);

    const updateURLParams = (params) => {
        const newParams = new URLSearchParams()

        // Add all non-default values to URL
        Object.entries(params).forEach(([key, value]) => {
            if (value && value !== 'all' && value !== '' && value !== 1 && value !== 10) {
                newParams.set(key, value)
            }
        })

        setSearchParams(newParams)
    }

    // Load reports with current filters and search
    const loadReports = async () => {
        await loadReportsWithFilters(filters, searchTerm)
    }

    // Toggle field selection for display
    const handleToggleField = (fieldId) => {
        // Don't allow deselection of empid
        if (fieldId === "empid") return;

        setSelectedFields(prev => {
            if (prev.includes(fieldId)) {
                return prev.filter(f => f !== fieldId);
            } else {
                return [...prev, fieldId];
            }
        });
    }

    const handleSelectAllFields = () => {
        setSelectedFields(fieldOptions.map(f => f.id))
    }

    const handleSelectNoneFields = () => {
        // Always keep empid selected
        setSelectedFields(["empid"])
    }

    // Export reports based on filters and search
    const handleExport = async () => {
        setExportLoading(true)
        try {
            const APIData = {
                department: filters.department !== 'all' ? filters.department : undefined,
                search: searchTerm,
                fields: selectedFields.join(",") || undefined
            }

            Object.keys(APIData).forEach(key => APIData[key] === undefined && delete APIData[key])

            const result = await exportLeaveBalanceReports(APIData)

            if (result.status === "success") {
                toast.success("Leave balance report exported successfully")
            } else {
                toast.error("Failed to export leave balance report")
            }
        } catch (error) {
            console.error("Error exporting:", error)
            if (error.response && error.response.status === 401) {
                toast.error("Authentication failed. Please log in again.")
            } else if (error.response && error.response.status === 500) {
                toast.error("Server error during export. Please try again later.")
            } else {
                toast.error("Error exporting leave balance report")
            }
        }
        setExportLoading(false)
        setFieldDialogOpen(false)
    }

    // Search by name and empid
    const handleSearch = (term) => {
        setSearchTerm(term)
        setPage(1)
    }

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Check if any filters are active
    const checkActiveFilters = () => {
        return (
            filters.department !== "all" ||
            searchTerm !== ""
        )
    }

    const applyFilters = () => {
        setPage(1)
        setFiltersApplied(true)
        loadReports()
        setFilterDialogOpen(false)
    }

    const clearFilters = () => {
        // Reset all filter states first
        const resetFilters = {
            department: "all"
        }

        setFilters(resetFilters)
        setSearchTerm("")
        setPage(1)
        setFiltersApplied(false)
        setSearchParams({})

        // Load reports with reset filters AFTER state updates
        setTimeout(() => {
            loadReportsWithFilters(resetFilters, "")
        }, 0)
    }

    const loadReportsWithFilters = async (filterValues, searchValue) => {
        setLoading(true)
        try {
            const APIData = {
                department: filterValues.department !== 'all' ? filterValues.department : undefined,
                search: searchValue,
                page: page,
                quantity: quantity
            }

            // Update URL with current filters
            updateURLParams({
                department: filterValues.department,
                search: searchValue,
                page: page,
                quantity: quantity
            })

            // Remove undefined values
            Object.keys(APIData).forEach(key => APIData[key] === undefined && delete APIData[key])

            const result = await fetchLeaveBalanceReports(APIData)

            if (result.status === "success") {
                setReports(result.data)
                setTotalItems(result.total)
                if (result.filters) {
                    setFilterOptions(result.filters)
                }
            } else {
                toast.error("Failed to fetch leave balance reports")
            }
        } catch (error) {
            console.error("Error loading reports:", error)
            toast.error("Error loading leave balance reports")
        }
        setLoading(false)
    }

    useEffect(() => {
        loadReports()
    }, [page, quantity, searchTerm])

    // Update hasActiveFilters whenever filters change
    useEffect(() => {
        setHasActiveFilters(checkActiveFilters())
    }, [filters, searchTerm])

    return (
        <>
            <Paper className="pagePaper">
                <span className="info">
                    <h5>Leave Balance Reports</h5>
                    <SearchBar searchText="Search by name..." onSearch={handleSearch} searchType="name" />
                </span>

                {/* Today's Date Display */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "30px" }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'var(--secondary-color)' }}>
                        Today's Date: {todayDate}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: "20px" }}>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon sx={{ color: "white" }} />}
                            onClick={() => setFieldDialogOpen(true)}
                            sx={{ backgroundColor: "var(--secondary-color)" }}
                        >
                            Export
                        </Button>
                    </Box>
                </Box>

                {/* Active Filters Display */}
                {hasActiveFilters && filtersApplied && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: "transparent",
                            borderRadius: 2,
                            border: "1px solid var(--text-secondary)",
                            transition: "all 0.3s ease-in-out",
                            animation: "fadeIn 0.3s ease-in-out"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <FilterListIcon sx={{ fontSize: 18, color: "var(--text-primary)", mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "var(--text-primary)" }}>
                                Active Filters
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                            {filters.department !== "all" && (
                                <Chip
                                    label={`Dept: ${filters.department}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    onDelete={() => {
                                        const newFilters = { ...filters, department: "all" }
                                        setFilters(newFilters)
                                        setPage(1)
                                        updateURLParams({
                                            ...newFilters,
                                            search: searchTerm,
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(newFilters, searchTerm)
                                    }}
                                />
                            )}

                            {searchTerm && (
                                <Chip
                                    label={`Search: "${searchTerm}"`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onDelete={() => {
                                        setSearchTerm("")
                                        setPage(1)
                                        updateURLParams({
                                            ...filters,
                                            search: "",
                                            page: 1,
                                            quantity: quantity
                                        })
                                        loadReportsWithFilters(filters, "")
                                    }}
                                />
                            )}

                            <Button
                                size="small"
                                sx={{
                                    fontSize: "0.75rem",
                                    height: "24px",
                                    ml: 1
                                }}
                                onClick={clearFilters}
                                variant="contained"
                                color="secondary"
                            >
                                Clear All
                            </Button>
                        </Box>
                    </Box>
                )}

                <div className="row" style={{ paddingTop: "20px" }}>
                    <div className="col-md-12">
                        <TableContainer sx={{ overflowY: "auto", maxHeight: "500px" }}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                className="tableHead"
                                                key={column.id}
                                                align="center"
                                                sx={{
                                                    minWidth: column.minWidth,
                                                    backgroundColor: "var(--card-color)",
                                                    color: "var(--primary-text-color)",
                                                    fontSize: "10px",
                                                    fontWeight: "bold"
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                                <CircularProgress size={100} style={{ color: "var(--secondary-color)" }} />
                                            </TableCell>
                                        </TableRow>
                                    ) : reports.length > 0 ? (
                                        reports.map((row, index) => {
                                            // Calculate leave entitlements for this employee
                                            const leaveEntitlements = calculateLeaveEntitlements(
                                                row.dateofjoin,
                                                row.leave_balances,
                                                row.gender
                                            );

                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={row.empid}>
                                                    {columns.map((column) => {
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align="center"
                                                                sx={{
                                                                    fontSize: "10px",
                                                                    color: "var(--primary-text-color)",
                                                                }}
                                                            >
                                                                {column.id === "id" ? (
                                                                    // Calculate serial number based on pagination
                                                                    totalItems - ((page - 1) * quantity + index)
                                                                ) : column.id === "dateofjoin" ? (
                                                                    row.dateofjoin ? row.dateofjoin : 'N/A'
                                                                ) : column.id === "probation_period" ? (
                                                                    calculateProbationPeriod(row.dateofjoin)
                                                                ) : column.id === "annual_balance" ? (
                                                                    leaveEntitlements.annual.display
                                                                ) : column.id === "sick_balance" ? (
                                                                    leaveEntitlements.sick.display
                                                                ) : column.id === "maternity_balance" ? (
                                                                    leaveEntitlements.maternity.display
                                                                ) : column.id === "paternity_balance" ? (
                                                                    leaveEntitlements.paternity.display
                                                                ) : column.id === "emergency_balance" ? (
                                                                    leaveEntitlements.emergency.display
                                                                ) : (
                                                                    row[column.id]
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                                <Typography color="textSecondary">
                                                    No employee records found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </div>

                <PaginationTable
                    totalItems={totalItems}
                    itemsPerPage={quantity}
                    onPageChange={setPage}
                    onItemsPerPageChange={setQuantity}
                />
            </Paper>

            {/* Filter Dialog */}
            <Dialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Filter Leave Balance Reports</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Department</InputLabel>
                                <Select
                                    value={filters.department}
                                    label="Department"
                                    onChange={(e) => handleFilterChange("department", e.target.value)}
                                    sx={{ color: "var(--text-secondary) !important" }}
                                >
                                    <MenuItem value="all">All Departments</MenuItem>
                                    {filterOptions.departments.map(dept => (
                                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    label="Month"
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                    <MenuItem value="all">All Months</MenuItem>
                                    <MenuItem value="1">January</MenuItem>
                                    <MenuItem value="2">February</MenuItem>
                                    <MenuItem value="3">March</MenuItem>
                                    <MenuItem value="4">April</MenuItem>
                                    <MenuItem value="5">May</MenuItem>
                                    <MenuItem value="6">June</MenuItem>
                                    <MenuItem value="7">July</MenuItem>
                                    <MenuItem value="8">August</MenuItem>
                                    <MenuItem value="9">September</MenuItem>
                                    <MenuItem value="10">October</MenuItem>
                                    <MenuItem value="11">November</MenuItem>
                                    <MenuItem value="12">December</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="Year"
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    <MenuItem value="all">All Years</MenuItem>
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = new Date().getFullYear() - i;
                                        return (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setFilterDialogOpen(false)}
                        variant="outlined"
                        sx={{ borderColor: "red", color: "red" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={applyFilters}
                        variant="contained"
                        sx={{ backgroundColor: "var(--secondary-color)" }}
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Field Selection Dialog */}
            <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Select Fields to Export</Typography>
                        <IconButton onClick={() => setFieldDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Box mb={1} display="flex" gap={1}>
                        <Button
                            size="small"
                            onClick={handleSelectAllFields}
                            variant="contained"
                            sx={{ backgroundColor: "purple" }}
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleSelectNoneFields}
                            variant="contained"
                            sx={{ backgroundColor: "red" }}
                        >
                            Select None
                        </Button>
                    </Box>

                    <FormGroup>
                        <Grid container spacing={1}>
                            {fieldOptions.map((field) => (
                                <Grid item xs={6} key={field.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedFields.includes(field.id)}
                                                onChange={() => handleToggleField(field.id)}
                                                disabled={field.id === "empid"}
                                                sx={{
                                                    color: "var(--secondary-color)",
                                                    '&.Mui-checked': {
                                                        color: "var(--secondary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: field.id === "empid" ? "var(--grey-500)" : "inherit" }}
                                                >
                                                    {field.label}
                                                </Typography>
                                                {field.id === "empid" && (
                                                    <Typography variant="caption" color="var(--grey-500)" sx={{ ml: 0.5 }}>
                                                        (Required)
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    <Box mt={1}>
                        <Typography variant="body2" paddingTop="10px" color="var(--text-secondary)">
                            Selected: {selectedFields.length} of {fieldOptions.length} fields
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        // disabled={exportLoading}
                        startIcon={exportLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <DownloadIcon sx={{ color: "white" }} />}
                        sx={{
                            backgroundColor: "var(--secondary-color)",
                            '&:hover': {
                                backgroundColor: "var(--secondary-color)"
                            }
                        }}
                        size="small"
                    >
                        Export
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}